import { createLogger } from '@graphistry/common/logger';
const logger = createLogger(__filename);

// formats
const COMPUTED_FORMATS = {
    RELATIVE_LINK: 'relative-link',
    LINK: 'link',
    TEXT_TEMPLATE: 'text-template'
};

// fields
const COMPUTED_FIELDS = {
    SOURCE: 'source_field',
    DISPLAY: 'display_field'
};

function applyFilter(label, filterValues, isExclusion = true) {
    const filteredColumns = label.columns.filter(col => {
        const shouldApply = filterValues[col.key];
        if (isExclusion) {
            return !shouldApply;
        } else {
            return shouldApply;
        }
    });
    return { ...label, columns: filteredColumns };
}

function deduplicateLabels(labels) {
    const seen = new Set();
    return labels.filter(label => {
        const key = `${label.type}-${label.globalIndex}`
        if (seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });
}

function filterLabelProperties(encodings, labels) {
    if (!encodings) {
        logger.debug("No encodings found, proceeding with default logic");
        return labels;
    }

    const processedLabels = [];
    ['point', 'edge'].forEach(type => {
        let typeLabels = labels.filter(label => label.type === type);  

        if (encodings[type]) {  
            const encodingConfig = encodings[type];
            
            typeLabels = typeLabels.map(label => {
                const labelType = label.columns.find(col => col.key === 'type')?.value;
                let hasInclusionRules = false;
                
                if (encodingConfig.fieldinclusion) {
                    hasInclusionRules = true;
                    const globalInclusions = Object.fromEntries(
                        Object.entries(encodingConfig.fieldinclusion.include || {})
                            .filter(([key]) => key !== 'types')
                    );

                    let inclusionRules = { ...globalInclusions };

                    if (labelType && encodingConfig.fieldinclusion.include?.types?.[labelType]) {
                        inclusionRules = {
                            ...inclusionRules,
                            ...encodingConfig.fieldinclusion.include.types[labelType]
                        };
                    }

                    label = applyFilter(label, inclusionRules, false);
                }
                
                if (encodingConfig.fieldexclusion?.exclude) {
                    const globalExclusions = Object.fromEntries(
                        Object.entries(encodingConfig.fieldexclusion.exclude || {})
                            .filter(([key]) => key !== 'types')
                    );

                    let exclusionRules = { ...globalExclusions };

                    if (labelType && encodingConfig.fieldexclusion.exclude?.types?.[labelType]) {
                        exclusionRules = {
                            ...exclusionRules,
                            ...encodingConfig.fieldexclusion.exclude.types[labelType]
                        };
                    }

                    label = applyFilter(label, exclusionRules, true);
                }
                
                return label;
            });
        }
        processedLabels.push(...typeLabels);  
    });
    return deduplicateLabels(processedLabels);
}

function processEncodingsComputed(encodings, labels) {
    if (!encodings) {
        return labels;
    }

    ['edge', 'point'].forEach(type => {
        const encodingConfig = encodings[type];
        const computedConfig = encodingConfig?.computed?.computed;
        const attribute = encodingConfig?.computed?.attribute;

        if (!computedConfig || !attribute) {
            return;
        }

        const filteredLabels = labels.filter(label => label.type === type);

        const transformedLabels = filteredLabels.map(label => {
            if (!label || !label.columns) {
                return label;
            }

            try {
                const labelType = label.columns.find(col => col.key === 'type')?.value;
                
                // type specific support for computed fields
                const typeConfig = labelType && computedConfig.types?.[labelType];
                const config = {
                    format: typeConfig?.format || computedConfig.format,
                    template: typeConfig?.template || computedConfig.template,
                    source_field: typeConfig?.source_field || computedConfig.source_field,
                    display_field: typeConfig?.display_field || computedConfig.display_field
                };

                if (computedConfig.types && !typeConfig && !computedConfig.template) {
                    return label;
                }

                const format = config.format;
                
                if (format === COMPUTED_FORMATS.TEXT_TEMPLATE) {
                    if (!config.template) {
                        logger.error('template is required for format: "text-template"');
                        return label;
                    }

                    const computedValue = config.template.replace(/\${([^}]+)}/g, (match, key) => {
                        const col = label.columns.find(c => c.key === key);
                        return col ? col.value : match;
                    });

                    label.columns = [...label.columns, {
                        key: attribute,
                        value: computedValue,
                        dataType: 'string'
                    }];
                } else if (format === COMPUTED_FORMATS.RELATIVE_LINK || format === COMPUTED_FORMATS.LINK) {
                    let url;
                    if (config.template) {
                        url = config.template.replace(/\${([^}]+)}/g, (match, key) => {
                            const col = label.columns.find(c => c.key === key);
                            return col ? col.value : match;
                        });
                    } else if (config.source_field) {
                        const sourceColumn = label.columns.find(c => c.key === config.source_field);
                        if (!sourceColumn) {
                            logger.error(`Source field ${config.source_field} not found`);
                            return label;
                        }
                        url = format === COMPUTED_FORMATS.RELATIVE_LINK 
                            ? `/graph/graph.html?dataset=${sourceColumn.value}`
                            : sourceColumn.value;
                    } else {
                        logger.error('Neither template nor source_field provided for link');
                        return label;
                    }
                    
                    if (!config.display_field) {
                        logger.error('display_field is required for links');
                        return label;
                    }
                    
                    const displayColumn = label.columns.find(c => c.key === config.display_field);
                    if (!displayColumn) {
                        logger.error(`Display field ${config.display_field} not found`);
                        return label;
                    }

                    label.columns = [...label.columns, {
                        key: attribute,
                        value: `<a href="${url}">${displayColumn.value}</a>`,
                        dataType: 'string'
                    }];
                }
                return label;
            } catch (error) {
                logger.error('Error processing transformation:', error);
                return label;
            }
        });

        labels = labels.map(label => {
            const transformedLabel = transformedLabels.find(tLabel => 
                tLabel.index === label.index && tLabel.type === type);
            return transformedLabel ? transformedLabel : label;
        });
    });
    
    return deduplicateLabels(labels);
}

function processEncodingsNulls(encodings, labels) {
    if (!encodings) {
        return labels;
    }

    ['edge', 'point'].forEach(type => {
        const encodingConfig = encodings[type];
        const showNulls = encodingConfig?.show;
        const nullValue = encodingConfig?.value || 'N/A';

        if (showNulls === undefined) {
            return;
        }

        const filteredLabels = labels.filter(label => label.type === type);
        if (!filteredLabels.length) {
            return;
        }

        const transformedLabels = filteredLabels.map(label => {
            if (!label || !label.columns) {
                return label;
            }

            try {
                label.columns = label.columns
                    .map(col => {
                        if (col.value === null || col.value === undefined) {
                            if (!showNulls) {
                                return null;
                            }
                            return {
                                ...col,
                                value: nullValue
                            };
                        }
                        return col;
                    })
                    .filter(Boolean);

                return label;
            } catch (error) {
                logger.error('Error processing null values:', error);
                return label;
            }
        });

        labels = labels.map(label => {
            if (label.type !== type) {
                return label;
            }
            const transformedLabel = transformedLabels.find(tLabel => 
                tLabel.index === label.index);
            return transformedLabel || label;
        });
    });

    return deduplicateLabels(labels);
}

export { 
    applyFilter, 
    filterLabelProperties, 
    processEncodingsComputed,
    processEncodingsNulls,
    COMPUTED_FORMATS,  
    COMPUTED_FIELDS 
};
